import { useEffect, useState } from 'react'
import { Loader2, Target, CheckCircle2, Clock, Star } from 'lucide-react'
import { api } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import type { Mission } from '@territory-nexus/shared'
import { MISSION_DEFINITIONS } from '@territory-nexus/shared'
import clsx from 'clsx'

export default function MissionsPanel() {
  const [missions, setMissions] = useState<Mission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchMissions = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await api.get<{ success: boolean; data: Mission[] }>('/missions')
      if (res.data.success) {
        setMissions(res.data.data)
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Failed to load missions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMissions()
    useAuthStore.getState().refreshPlayer()
  }, [])

  const completedCount = missions.filter(m => m.completed).length
  const totalXp = missions.reduce((sum, m) => sum + (m.completed ? m.xpReward : 0), 0)

  const getTimeRemaining = (expiresAt: Date | null): string => {
    if (!expiresAt) return ''
    const now = Date.now()
    const diff = new Date(expiresAt).getTime() - now
    if (diff <= 0) return 'Expired'
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours}h ${mins}m`
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 text-sm mb-3">{error}</p>
          <button
            onClick={fetchMissions}
            className="px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 text-sm hover:bg-cyan-500/30 transition"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-display font-bold text-white flex items-center gap-2">
            <Target className="w-5 h-5 text-amber-400" />
            Daily Missions
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {completedCount}/{missions.length} complete
            {totalXp > 0 && <span className="text-cyan-400 ml-2">+{totalXp} XP earned</span>}
          </p>
        </div>
        {missions.length > 0 && missions[0].expiresAt && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <Clock className="w-3.5 h-3.5" />
            {getTimeRemaining(missions[0].expiresAt)}
          </div>
        )}
      </div>

      {/* Mission List */}
      <div className="space-y-2">
        {missions.map(mission => {
          const def = MISSION_DEFINITIONS[mission.missionType]
          const progress = Math.min(mission.currentCount / mission.targetCount, 1)

          return (
            <div
              key={mission.id}
              className={clsx(
                'rounded-xl border p-3 transition-all',
                mission.completed
                  ? 'border-green-500/20 bg-green-500/5'
                  : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                  <div className={clsx(
                    'mt-0.5 flex-shrink-0',
                    mission.completed ? 'text-green-400' : 'text-slate-500'
                  )}>
                    {mission.completed
                      ? <CheckCircle2 className="w-5 h-5" />
                      : <Target className="w-5 h-5" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={clsx(
                      'text-sm font-medium',
                      mission.completed ? 'text-green-300 line-through opacity-70' : 'text-white'
                    )}>
                      {def.title}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{def.description}</div>
                  </div>
                </div>

                {/* XP Reward */}
                <div className={clsx(
                  'flex items-center gap-1 text-xs font-medium flex-shrink-0 px-2 py-1 rounded-lg',
                  mission.completed
                    ? 'bg-green-500/10 text-green-400'
                    : 'bg-amber-500/10 text-amber-400'
                )}>
                  <Star className="w-3 h-3" />
                  +{mission.xpReward} XP
                </div>
              </div>

              {/* Progress Bar */}
              {!mission.completed && (
                <div className="mt-2.5 ml-[30px]">
                  <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                    <span>Progress</span>
                    <span>{mission.currentCount}/{mission.targetCount}</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                      style={{ width: `${progress * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {missions.length === 0 && (
        <div className="text-center py-12">
          <Target className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No missions available</p>
          <p className="text-slate-600 text-xs mt-1">Check back later</p>
        </div>
      )}
    </div>
  )
}
